import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { Acuity, ArrivalMode, AttendanceSource, ChiefOfComplaintECDS } from '../../../../controls/agyle/ecds';
import moment from 'moment';
import config from '../../../../app/config';
import _ from 'lodash';
import { AttendanceCategory } from '../../../../controls/agyle/dictionaries';
import { UtilsMomentJS } from '../../../../store/utilsMoment';
import { useStylesECDS } from './ecdsStyle';
import { useSelector } from 'react-redux';
import { acuity, arrivalModes, attendanceCategory, attendanceSource, chiefComplaint } from '../../../../store/reducers/dicts';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import { calculateArrivalDate } from '../../../../util/patientUtil';
import { cleanObject } from '../../../../util/patientContextUtil';

const AttendanceActivityV3 = (props) => {
    const { t } = useTranslation();
    const classes = useStylesECDS();
    const { version, isSmall } = props;

    //Attendance Activity Characteristics Dataset
    const [attendanceActivity, setAttendanceActivity] = useState({
        EmergencyCareAttendanceIdentifier: '',
        EmergencyCareArrivalMode_SnomedCt: null,
        EmergencyCareAttendanceCategory: null,
        EmergencyCareAttendanceSource_SnomedCt: null,
        OrganisationSiteIdentifier_EmergencyCareAttendanceSource: '',
        EmergencyCareArrivalDate: null,
        EmergencyCareArrivalTime: null,
        AgeAtCdsActivityDate: '',
        EmergencyCareInitialAssessmentDate: null,
        EmergencyCareInitialAssessmentTime: null,
        EmergencyCareAcuity_SnomedCt: null,
        EmergencyCareChiefComplaint_SnomedCt: null,
        EmergencyCareDateSeenForTreatment: null,
        EmergencyCareTimeSeenForTreatment: null,
    });
    const [pickers, setPickers] = useState({
        EmergencyCareInitialAssessmentDate: null,
        EmergencyCareInitialAssessmentTime: null,
        EmergencyCareDateSeenForTreatment: null,
        EmergencyCareTimeSeenForTreatment: null,
    });

    // Dictionaries
    const acuityDict = useSelector(acuity);
    const arrivalModesDict = useSelector(arrivalModes);
    const attendanceSourceDict = useSelector(attendanceSource);
    const attendanceCategoryDict = useSelector(attendanceCategory);
    const chiefComplaintDict = useSelector(chiefComplaint);

    // For ReadOnly Data
    const disableData = useRef({});

    // Validation
    const validationEnabled = config?.modules?.forms?.ecds?.validation || false;
    const [error, setError] = useState({
        attendanceId: validationEnabled,
        arrival: false,
        attendanceCat: false,
        attendanceSource: false,
        orgId: false,
        arrivalDate: validationEnabled,
        arrivalTime: validationEnabled,
        age: validationEnabled,
        assessmentDate: false,
        assessmentTime: false,
        acuity: false,
        chiefOfComplaint: false,
        treatDate: false,
        treatTime: false,
    });

    // invalid pickers
    const errorPicker = useRef({
        arrivalDate: null,
        arrivalTime: null,
        assessmentDate: null,
        assessmentTime: null,
        seenDate: null,
        seenTime: null,
    });

    // Initial Data
    useEffect(() => {
        let pickers = {};
        let data = {};
        let dataToBlock = {};
        let arrivalDate;

        if (props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.EmergencyCareAttendanceActivityCharacteristics) {
            const clone = _.cloneDeep(
                props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.EmergencyCareAttendanceActivityCharacteristics,
            );
            const localInitAss = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
                clone.EmergencyCareInitialAssessmentDate,
                clone.EmergencyCareInitialAssessmentTime,
            );
            const localSeen = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
                clone.EmergencyCareDateSeenForTreatment,
                clone.EmergencyCareTimeSeenForTreatment,
            );

            data = {
                AgeAtCdsActivityDate: clone.AgeAtCdsActivityDate,
                EmergencyCareAcuity_SnomedCt:
                    clone.EmergencyCareAcuity_SnomedCt !== ''
                        ? _.find(acuityDict, {
                              ecdsVersion: version,
                              snomedCodes: clone.EmergencyCareAcuity_SnomedCt,
                          })
                        : null,
                EmergencyCareArrivalDate: clone.EmergencyCareArrivalDate ? moment(clone.EmergencyCareArrivalDate) : null,
                EmergencyCareArrivalMode_SnomedCt:
                    clone.EmergencyCareArrivalMode_SnomedCt !== ''
                        ? _.find(arrivalModesDict, {
                              ecdsVersion: version,
                              snomedCodes: clone.EmergencyCareArrivalMode_SnomedCt,
                          })
                        : null,
                EmergencyCareArrivalTime: clone.EmergencyCareArrivalTime
                    ? moment(clone.EmergencyCareArrivalTime, config.ecdsTimeFormat)
                    : null,
                EmergencyCareAttendanceCategory: clone.EmergencyCareAttendanceCategory
                    ? _.find(attendanceCategoryDict, { __code: clone.EmergencyCareAttendanceCategory })
                    : null,
                EmergencyCareAttendanceIdentifier: clone.EmergencyCareAttendanceIdentifier,
                EmergencyCareAttendanceSource_SnomedCt:
                    clone.EmergencyCareAttendanceSource_SnomedCt !== ''
                        ? _.find(attendanceSourceDict, {
                              ecdsVersion: version,
                              snomedCodes: clone.EmergencyCareAttendanceSource_SnomedCt,
                          })
                        : null,
                EmergencyCareChiefComplaint_SnomedCt:
                    clone.EmergencyCareChiefComplaint_SnomedCt !== ''
                        ? _.find(chiefComplaintDict, {
                              ecdsVersion: version,
                              snomedCodes: clone.EmergencyCareChiefComplaint_SnomedCt,
                          })
                        : null,
                EmergencyCareInitialAssessmentDate: clone.EmergencyCareInitialAssessmentDate
                    ? moment(clone.EmergencyCareInitialAssessmentDate)
                    : null,
                EmergencyCareInitialAssessmentTime: clone.EmergencyCareInitialAssessmentTime
                    ? moment(clone.EmergencyCareInitialAssessmentTime, config.ecdsTimeFormat)
                    : null,
                EmergencyCareDateSeenForTreatment: clone.EmergencyCareDateSeenForTreatment
                    ? moment(clone.EmergencyCareDateSeenForTreatment)
                    : null,
                EmergencyCareTimeSeenForTreatment: clone.EmergencyCareTimeSeenForTreatment
                    ? moment(clone.EmergencyCareTimeSeenForTreatment, config.ecdsTimeFormat)
                    : null,
                OrganisationSiteIdentifier_EmergencyCareAttendanceSource: clone.OrganisationSiteIdentifier_EmergencyCareAttendanceSource,
            };

            pickers = {
                EmergencyCareInitialAssessmentDate: clone.EmergencyCareInitialAssessmentDate && localInitAss ? localInitAss : null,
                EmergencyCareInitialAssessmentTime: clone.EmergencyCareInitialAssessmentTime && localInitAss ? localInitAss : null,
                EmergencyCareDateSeenForTreatment: clone.EmergencyCareDateSeenForTreatment && localSeen ? localSeen : null,
                EmergencyCareTimeSeenForTreatment: clone.EmergencyCareTimeSeenForTreatment && localSeen ? localSeen : null,
            };
        }

        const arrival = props.patient.admission?.arrivalModeCode;
        let ambulance = [];
        _.forEach(_.filter(props.dictionaries?.arrival, { group: 'ambulance' }), (element) => {
            ambulance.push(element.__code);
        });

        if (!_.isEmpty(ambulance) && ambulance.includes(arrival)) {
            arrivalDate = moment(calculateArrivalDate(props.patient), config.defaultDateTimeFormat);
        } else {
            arrivalDate = moment(props.patient?.admission?.startDate);
        }

        dataToBlock = {
            EmergencyCareArrivalDate: arrivalDate,
            EmergencyCareArrivalTime: arrivalDate,
        };

        if (acuityDict && props.patient?.triage?.discriminator) {
            const priority = props.patient.triage?.discriminator?.snomedCode;
            let acuity = _.find(acuityDict, (a) => a.ecdsVersion === version && _.startsWith(a.snomedCode, priority));
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareAcuity_SnomedCt: acuity,
            };
        }

        if (!dataToBlock?.EmergencyCareAcuity_SnomedCt && acuityDict && props.patient?.initialAssessment?.discriminator) {
            const priority = props.patient.initialAssessment?.discriminator?.snomedCode;
            let acuity = _.find(acuityDict, (a) => a.ecdsVersion === version && _.startsWith(a.snomedCode, priority));
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareAcuity_SnomedCt: acuity,
            };
        }

        if (!dataToBlock?.EmergencyCareAcuity_SnomedCt && acuityDict && props.patient?.triage?.firstDiscriminator) {
            const priority = props.patient.triage.firstDiscriminator?.snomedCode;
            let acuity = _.find(acuityDict, (a) => a.ecdsVersion === version && _.startsWith(a.snomedCode, priority));
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareAcuity_SnomedCt: acuity,
            };
        }

        if (arrivalModesDict && props.patient.admission?.arrivalModeAltCode) {
            const arrival = props.patient.admission?.arrivalModeAltCode;
            let result = _.find(arrivalModesDict, (am) => am.ecdsVersion === version && _.startsWith(am.snomedCode, arrival));
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareArrivalMode_SnomedCt: result,
            };
        }

        if (attendanceSourceDict && props.patient.admission?.admitSourceCode) {
            let result = _.find(attendanceSourceDict, {
                ecdsVersion: version,
                snomedCode: props.patient.admission?.admitSourceCode,
            });
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareAttendanceSource_SnomedCt: result || null,
            };
        }

        if (
            chiefComplaintDict &&
            props.patient.admission?.initialAEComplaintSnomedCode &&
            !attendanceActivity.EmergencyCareChiefComplaint_SnomedCt
        ) {
            const chief = props.patient.admission?.initialAEComplaintSnomedCode;
            let result = null;
            if (chief) {
                chiefComplaintDict.forEach((el) => {
                    if (el.ecdsVersion === version && _.startsWith(el.snomedCode, chief)) {
                        result = el;
                    }
                });
            }
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareChiefComplaint_SnomedCt: result,
            };
        }

        if (props.patient.patient?.dob) {
            dataToBlock = {
                ...dataToBlock,
                AgeAtCdsActivityDate: arrivalDate.diff(moment(props.patient.patient.dob), 'years') + '',
            };
        }

        if (props.patient.admission) {
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareAttendanceIdentifier: props.patient.admission.attendanceId || '',
            };
        }

        if (props.patient.triage) {
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareChiefComplaint_SnomedCt: props.patient.triage.chiefComplaint || null,
                EmergencyCareInitialAssessmentDate: props.patient.triage.firstWhen ? moment(props.patient.triage.firstWhen) : null,
                EmergencyCareInitialAssessmentTime: props.patient.triage.firstWhen ? moment(props.patient.triage.firstWhen) : null,
            };

            pickers = {
                ...pickers,
                EmergencyCareInitialAssessmentDate: props.patient.triage.firstWhen ? moment(props.patient.triage.firstWhen) : null,
                EmergencyCareInitialAssessmentTime: props.patient.triage.firstWhen ? moment(props.patient.triage.firstWhen) : null,
            };
        }

        if (props.patient?.metadata?.attendanceCategory) {
            const find = _.find(attendanceCategoryDict, { __code: props.patient?.metadata?.attendanceCategory?.code });
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareAttendanceCategory: find || null,
            };
        }

        // KPI Time to Initial Assessment
        // Triage Start Time
        // Ambulance Handover Time
        // See and Treat Time
        // Rapid Assessment Time
        if (props.patient?.metadata?.firstInitialAssessment?.when) {
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareInitialAssessmentDate: moment(props.patient?.metadata?.firstInitialAssessment?.when) || null,
                EmergencyCareInitialAssessmentTime: moment(props.patient?.metadata?.firstInitialAssessment?.when) || null,
            };

            pickers = {
                ...pickers,
                EmergencyCareInitialAssessmentDate: moment(props.patient?.metadata?.firstInitialAssessment?.when) || null,
                EmergencyCareInitialAssessmentTime: moment(props.patient?.metadata?.firstInitialAssessment?.when) || null,
            };
        }

        // KPI Time to Treatment
        // See and Treat Time
        // Rapid Assessment Time
        // Clerking Clinician Set Time
        // Clerking Miniform started time
        if (props.patient?.metadata?.firstClinicianSaved?.when) {
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareTimeSeenForTreatment: moment(props.patient?.metadata?.firstClinicianSaved?.when) || null,
                EmergencyCareDateSeenForTreatment: moment(props.patient?.metadata?.firstClinicianSaved?.when) || null,
            };

            pickers = {
                ...pickers,
                EmergencyCareTimeSeenForTreatment: moment(props.patient?.metadata?.firstClinicianSaved?.when) || null,
                EmergencyCareDateSeenForTreatment: moment(props.patient?.metadata?.firstClinicianSaved?.when) || null,
            };
        }

        data = {
            ...data,
            ...dataToBlock,
        };

        setAttendanceActivity((prevState) => ({ ...prevState, ...data }));
        disableData.current = { ...cleanObject(dataToBlock) };
        setPickers({ ...pickers });
    }, []);

    const setDataDictionary = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    const updateData = () => {
        const arrivalUTC = UtilsMomentJS.getUTCDateFromECDSFormat(
            attendanceActivity.EmergencyCareArrivalDate,
            attendanceActivity.EmergencyCareArrivalTime,
        );
        const initAssUTC = UtilsMomentJS.getUTCDateFromECDSFormat(
            pickers.EmergencyCareInitialAssessmentDate,
            pickers.EmergencyCareInitialAssessmentTime,
        );
        const seenUTC = UtilsMomentJS.getUTCDateFromECDSFormat(
            pickers.EmergencyCareDateSeenForTreatment,
            pickers.EmergencyCareTimeSeenForTreatment,
        );

        const data = {
            EmergencyCareAttendanceIdentifier: attendanceActivity.EmergencyCareAttendanceIdentifier,
            EmergencyCareArrivalMode_SnomedCt: attendanceActivity.EmergencyCareArrivalMode_SnomedCt
                ? attendanceActivity.EmergencyCareArrivalMode_SnomedCt.snomedCode
                : '',
            EmergencyCareAttendanceCategory: attendanceActivity.EmergencyCareAttendanceCategory
                ? attendanceActivity.EmergencyCareAttendanceCategory.__code
                : '',
            EmergencyCareAttendanceSource_SnomedCt: attendanceActivity.EmergencyCareAttendanceSource_SnomedCt
                ? attendanceActivity.EmergencyCareAttendanceSource_SnomedCt.snomedCode
                : '',
            OrganisationSiteIdentifier_EmergencyCareAttendanceSource:
                attendanceActivity.OrganisationSiteIdentifier_EmergencyCareAttendanceSource,
            EmergencyCareArrivalDate: arrivalUTC ? arrivalUTC.format(config.ecdsDateFormat) : '',
            EmergencyCareArrivalTime: arrivalUTC ? arrivalUTC.format(config.ecdsTimeFormat) : '',
            AgeAtCdsActivityDate: attendanceActivity.AgeAtCdsActivityDate,
            EmergencyCareInitialAssessmentDate: initAssUTC ? initAssUTC.format(config.ecdsDateFormat) : '',
            EmergencyCareInitialAssessmentTime: initAssUTC ? initAssUTC.format(config.ecdsTimeFormat) : '',
            EmergencyCareAcuity_SnomedCt: attendanceActivity.EmergencyCareAcuity_SnomedCt
                ? attendanceActivity.EmergencyCareAcuity_SnomedCt.snomedCode
                : '',
            EmergencyCareChiefComplaint_SnomedCt: attendanceActivity.EmergencyCareChiefComplaint_SnomedCt
                ? attendanceActivity.EmergencyCareChiefComplaint_SnomedCt.snomedCode
                : '',
            EmergencyCareDateSeenForTreatment: seenUTC ? seenUTC.format(config.ecdsDateFormat) : '',
            EmergencyCareTimeSeenForTreatment: seenUTC ? seenUTC.format(config.ecdsTimeFormat) : '',
        };
        props.setData(data);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (validate()) {
                updateData();
            }
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [attendanceActivity, pickers]);

    const changeHandler = (event) => {
        setAttendanceActivity((prevState) => ({ ...prevState, [event.target.name]: event.target.value }));
    };

    const calculateMinTime = useCallback(() => {
        const arrival = props.patient.admission?.arrivalModeCode;
        let ambulance = [];
        _.forEach(_.filter(props.dictionaries?.arrival, { group: 'ambulance' }), (element) => {
            ambulance.push(element.__code);
        });
        const isArrivalMode = !_.isEmpty(ambulance) && ambulance.includes(arrival);

        if (attendanceActivity.EmergencyCareArrivalDate) {
            if (
                moment(attendanceActivity.EmergencyCareArrivalDate).format('L') ===
                moment(moment(calculateArrivalDate(props.patient))).format('L')
            ) {
                return isArrivalMode ? moment(calculateArrivalDate(props.patient)) : moment(props.patient.admission.startDate);
            }
        }
        return null;
    }, [props.patient]);

    const validate = () => {
        let check = true;

        if (validationEnabled) {
            const errorData = {
                arrivalDate: !attendanceActivity.EmergencyCareArrivalDate,
                arrivalTime: !attendanceActivity.EmergencyCareArrivalTime,
                attendanceId: !props.checkLenght(1, 12, attendanceActivity.EmergencyCareAttendanceIdentifier),
                arrival: attendanceActivity.EmergencyCareArrivalMode_SnomedCt
                    ? !props.checkLenght(6, 18, attendanceActivity.EmergencyCareArrivalMode_SnomedCt)
                    : false,
                attendanceCat: attendanceActivity.EmergencyCareAttendanceCategory
                    ? !props.checkLenght(1, 1, attendanceActivity.EmergencyCareAttendanceCategory.value)
                    : false,
                attendanceSource: attendanceActivity.EmergencyCareAttendanceSource_SnomedCt
                    ? !props.checkLenght(6, 18, attendanceActivity.EmergencyCareAttendanceSource_SnomedCt)
                    : false,
                orgId:
                    attendanceActivity.OrganisationSiteIdentifier_EmergencyCareAttendanceSource !== ''
                        ? !props.checkLenght(5, 9, attendanceActivity.OrganisationSiteIdentifier_EmergencyCareAttendanceSource)
                        : false,
                age: !props.checkLenght(1, 3, attendanceActivity.AgeAtCdsActivityDate),
                acuity: attendanceActivity.EmergencyCareAcuity_SnomedCt
                    ? !props.checkLenght(6, 18, attendanceActivity.EmergencyCareAcuity_SnomedCt)
                    : false,
                chiefOfComplaint: attendanceActivity.EmergencyCareChiefComplaint_SnomedCt
                    ? !props.checkLenght(6, 18, attendanceActivity.EmergencyCareChiefComplaint_SnomedCt)
                    : false,
            };
            setError((prevState) => ({ ...prevState, ...errorData }));

            for (let prop in errorData) {
                if (errorData[prop]) {
                    check = false;
                    break;
                }
            }

            if (props.valid) {
                const validObj = {
                    name: 'Emergency Care Attendance Activity Characteristics',
                    groupStatus: 'M',
                    mandatory:
                        !attendanceActivity.EmergencyCareAttendanceIdentifier ||
                        !attendanceActivity.AgeAtCdsActivityDate ||
                        !attendanceActivity.EmergencyCareArrivalDate ||
                        !attendanceActivity.EmergencyCareArrivalTime,
                    errors: !check,
                    pickers:
                        !!errorPicker.current.arrivalDate ||
                        !!errorPicker.current.arrivalTime ||
                        !!errorPicker.current.assessmentDate ||
                        !!errorPicker.current.assessmentTime ||
                        !!errorPicker.current.seenDate ||
                        !!errorPicker.current.seenTime,
                };
                props.valid((prevState) => ({ ...prevState, activityChar: validObj }));
            }
        }
        return check;
    };

    const dateHandler = (name, event) => {
        const date = moment(event).set('second', 0);
        setPickers((prevState) => ({ ...prevState, [name]: date }));
    };

    if (props.accordion !== 'Emergency Care Attendance Activity Characteristics') return null;

    return (
        <div className={classes.root}>
            <Grid container spacing={2} style={{ marginTop: 1 }}>
                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="EmergencyCareAttendanceIdentifier"
                        label={t('Attendance Identifier')}
                        onChange={changeHandler}
                        value={attendanceActivity.EmergencyCareAttendanceIdentifier || ''}
                        error={error.attendanceId}
                        helperText={error.attendanceId ? 'Input must be between 1 and 12 characters long.' : ''}
                        maxLength={12}
                        minLength={1}
                        readOnly={!!disableData?.current.EmergencyCareAttendanceIdentifier}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <ArrivalMode
                        name="EmergencyCareArrivalMode_SnomedCt"
                        label={t('Arrival Mode')}
                        color="primary"
                        value={attendanceActivity.EmergencyCareArrivalMode_SnomedCt || null}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareArrivalMode_SnomedCt', val])}
                        error={error.arrival}
                        disabled={!!disableData?.current.EmergencyCareArrivalMode_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        name="EmergencyCareArrivalDate"
                        label={t('Arrival Date')}
                        type="date"
                        value={attendanceActivity.EmergencyCareArrivalDate || null}
                        onChange={() => null}
                        onError={(event) =>
                            (errorPicker.current = {
                                ...errorPicker.current,
                                arrivalDate: event,
                            })
                        }
                        error={error.arrivalDate}
                        readOnly={!!disableData?.current.EmergencyCareArrivalDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        name="EmergencyCareArrivalTime"
                        label={'Arrival Time'}
                        value={attendanceActivity.EmergencyCareArrivalTime || null}
                        onChange={() => null}
                        type="time"
                        onError={(event) =>
                            (errorPicker.current = {
                                ...errorPicker.current,
                                arrivalTime: event,
                            })
                        }
                        error={error.arrivalTime}
                        readOnly={!!disableData?.current.EmergencyCareArrivalTime}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AttendanceCategory
                        name="EmergencyCareAttendanceCategory"
                        label={t('Emergency Care Attendance Category')}
                        color="primary"
                        value={attendanceActivity.EmergencyCareAttendanceCategory}
                        disabled={!!disableData?.current.EmergencyCareAttendanceCategory}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareAttendanceCategory', val])}
                        error={error.attendanceCat}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AttendanceSource
                        name="EmergencyCareAttendanceSource_SnomedCt"
                        label={t('Attendance Source')}
                        color="primary"
                        value={attendanceActivity.EmergencyCareAttendanceSource_SnomedCt || null}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareAttendanceSource_SnomedCt', val])}
                        error={error.attendanceSource}
                        disabled={!!disableData?.current.EmergencyCareAttendanceSource_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="OrganisationSiteIdentifier_EmergencyCareAttendanceSource"
                        label={t('Org Site Id - Emergency Care Attendance Source')}
                        onChange={changeHandler}
                        value={attendanceActivity.OrganisationSiteIdentifier_EmergencyCareAttendanceSource || ''}
                        error={error.orgId}
                        helperText={error.orgId ? 'Input must be between 5 and 9 characters long.' : ''}
                        maxLength={9}
                        minLength={5}
                        readOnly={!!disableData?.current.OrganisationSiteIdentifier_EmergencyCareAttendanceSource}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="AgeAtCdsActivityDate"
                        label={t('Age at CDS Activity Date')}
                        type="number"
                        onChange={changeHandler}
                        value={attendanceActivity.AgeAtCdsActivityDate || ''}
                        error={error.age}
                        helperText={error.age ? 'Input must be max 3 characters long.' : ''}
                        readOnly={!!disableData?.current.AgeAtCdsActivityDate}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <ChiefOfComplaintECDS
                        name="EmergencyCareChiefComplaint_SnomedCt"
                        label={t('Chief Complaint')}
                        color="primary"
                        value={attendanceActivity.EmergencyCareChiefComplaint_SnomedCt || null}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareChiefComplaint_SnomedCt', val])}
                        error={error.chiefOfComplaint}
                        disabled={!!disableData?.current.EmergencyCareChiefComplaint_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Acuity
                        name="EmergencyCareAcuity_SnomedCt"
                        label={t('Acuity')}
                        color="primary"
                        value={attendanceActivity.EmergencyCareAcuity_SnomedCt || null}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareAcuity_SnomedCt', val])}
                        error={error.acuity}
                        disabled={!!disableData?.current.EmergencyCareAcuity_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        name="EmergencyCareInitialAssessmentDate"
                        label={t('Initial Assessment Date')}
                        type="date"
                        value={pickers.EmergencyCareInitialAssessmentDate || null}
                        onChange={(e) => dateHandler('EmergencyCareInitialAssessmentDate', e)}
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={(event) =>
                            (errorPicker.current = {
                                ...errorPicker.current,
                                assessmentDate: event,
                            })
                        }
                        error={error.assessmentDate}
                        readOnly={!!disableData?.current.EmergencyCareInitialAssessmentDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        name="EmergencyCareInitialAssessmentTime"
                        label={t('Initial Assessment Time')}
                        type="time"
                        value={pickers.EmergencyCareInitialAssessmentTime || null}
                        onChange={(e) => dateHandler('EmergencyCareInitialAssessmentTime', e)}
                        minTime={
                            pickers.EmergencyCareInitialAssessmentDate
                                ? moment(pickers.EmergencyCareInitialAssessmentDate).format('L') ===
                                  moment(moment(calculateArrivalDate(props.patient))).format('L')
                                    ? moment(calculateArrivalDate(props.patient))
                                    : null
                                : null
                        }
                        maxTime={
                            pickers.EmergencyCareInitialAssessmentDate
                                ? moment(pickers.EmergencyCareInitialAssessmentDate).format('L') === moment().format('L')
                                    ? moment()
                                    : null
                                : null
                        }
                        onError={(event) =>
                            (errorPicker.current = {
                                ...errorPicker.current,
                                assessmentTime: event,
                            })
                        }
                        error={error.assessmentTime}
                        readOnly={!!disableData?.current.EmergencyCareInitialAssessmentTime}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        name="EmergencyCareDateSeenForTreatment"
                        label={t('Date Seen for Treatment')}
                        type="date"
                        value={pickers.EmergencyCareDateSeenForTreatment || null}
                        onChange={(e) => dateHandler('EmergencyCareDateSeenForTreatment', e)}
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={(event) =>
                            (errorPicker.current = {
                                ...errorPicker.current,
                                seenDate: event,
                            })
                        }
                        error={error.treatDate}
                        readOnly={!!disableData?.current.EmergencyCareDateSeenForTreatment}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        name="EmergencyCareTimeSeenForTreatment"
                        label={t('Time Seen for Treatment')}
                        type="time"
                        value={pickers.EmergencyCareTimeSeenForTreatment || null}
                        onChange={(e) => dateHandler('EmergencyCareTimeSeenForTreatment', e)}
                        minTime={
                            pickers.EmergencyCareDateSeenForTreatment
                                ? moment(pickers.EmergencyCareDateSeenForTreatment).format('L') ===
                                  moment(moment(calculateArrivalDate(props.patient))).format('L')
                                    ? moment(calculateArrivalDate(props.patient))
                                    : null
                                : null
                        }
                        maxTime={
                            pickers.EmergencyCareDateSeenForTreatment
                                ? moment(pickers.EmergencyCareDateSeenForTreatment).format('L') === moment().format('L')
                                    ? moment()
                                    : null
                                : null
                        }
                        onError={(event) =>
                            (errorPicker.current = {
                                ...errorPicker.current,
                                seenTime: event,
                            })
                        }
                        error={error.treatTime}
                        readOnly={!!disableData?.current.EmergencyCareTimeSeenForTreatment}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default AttendanceActivityV3;
